/**
 * Semantic linter rules for AnimationPlayer. linterParser.ts validates the format during strict
 * parsing. These rules need the full scene.
 */
import { ruleRegistry } from '../../../linter/RuleRegistry.js';
import { extractLibraries, isActive } from './parser.js';
import { EXT_RESOURCE_CALL_ANYWHERE_RE } from '../../../godot/variant/resourceRef.js';
import { literalText } from '../../../godot/string/string.js';
import { hasUnresolvableClips, resolveAnimations } from './animationResolver.js';
import { armEmits, groundedArm, reportArm } from '../../../linter/ruleArms.js';
const arms = {
    autoplayMissing: groundedArm('animationplayer-autoplay-missing', {
        kind: 'no-engine-counterpart',
        scope: 'dangling-reference',
        because: 'no library the file declares holds a clip under that name',
    }),
    currentAnimationMissing: {
        severity: 'error',
        ruleName: 'animationplayer-current-animation-missing',
        grounding: { kind: 'engine', at: 'animation_player.cpp:429' },
    },
    inactive: groundedArm('animationplayer-inactive', {
        kind: 'engine-inert',
        at: 'animation_mixer.cpp:446',
        unused: 'processing is gated on active, so autoplay and current_animation never advance',
    }),
};
/**
 * Validate AnimationPlayer semantic rules
 */
function checkAnimationPlayer(context) {
    const diagnostics = [];
    const { node, scene } = context;
    const rawProps = node.rawProperties;
    // `speed_scale` gets no diagnostic at all: animation_player.cpp:1048 hints
    // "-4,4,0.001,or_less,or_greater", so both ends are open, and set_speed_scale
    // (:648) is a bare assignment. Negative is reverse playback; 0 pauses.
    // Which clip sources the file declares, and in which form: pre-4.0 files put
    // clips in an `anims/<name>` section, Godot 4 references AnimationLibraries
    // through `libraries/<name>` keys (the empty-name default library is written
    // `libraries/`), and older 4.x files use a single `libraries` dict.
    const hasAnimations = Object.keys(rawProps).some((key) => key.startsWith('anims/'));
    const hasLibraries = Object.keys(rawProps).some((key) => key === 'libraries' || key.startsWith('libraries/'));
    // Build the set of known clip names. Godot references a clip in the default
    // (empty-name) library bare ("walk") but a clip in a named library prefixed
    // ("combat/walk"), so resolve each library separately and prefix accordingly
    // (resolveAnimations flattens the library name away, hence the per-ref loop).
    const knownClips = new Set();
    for (const ref of extractLibraries(rawProps)) {
        const prefix = ref.name ? `${ref.name}/` : '';
        for (const anim of resolveAnimations([ref], scene.internalResources)) {
            knownClips.add(prefix + anim.name);
        }
    }
    // Legacy anims/ clips (pre-4.0): `anims/<clipname> = SubResource(...)`.
    for (const key of Object.keys(rawProps)) {
        if (key.startsWith('anims/'))
            knownClips.add(key.slice('anims/'.length));
    }
    // A clip is claimed missing only when the clip set is fully enumerable. An ExtResource library,
    // or an inline library mapping a clip to its own `.tres`, holds clips `resolveAnimations`
    // cannot see (it reads only SubResource entries), so a claim there would false-positive.
    const hasUnresolvableLibrary = Object.entries(rawProps).some(([key, value]) => (key === 'libraries' || key.startsWith('libraries/')) && EXT_RESOURCE_CALL_ANYWHERE_RE.test(value)) || hasUnresolvableClips(extractLibraries(rawProps), scene.internalResources);
    // A resolvable but empty library is still enumerable. A file with no clip source is not: a
    // script may add the clips, so nothing here can call a reference dangling.
    const canCheckExistence = (hasAnimations || hasLibraries) && !hasUnresolvableLibrary;
    // An empty StringName `&""` means no autoplay, so the guard reads the stripped name. Godot raises
    // no warning: `NOTIFICATION_READY` (animation_player.cpp:149-155) gates the autoplay dispatch on
    // `animation_set.has(autoplay)`, so a name that resolves to nothing never calls `play()`.
    if (canCheckExistence && rawProps.autoplay !== undefined) {
        const autoplayName = literalText(rawProps.autoplay);
        if (autoplayName.length > 0 && !knownClips.has(autoplayName)) {
            reportArm(diagnostics, arms.autoplayMissing, node, `AnimationPlayer 'autoplay' references animation "${autoplayName}" which may not exist. Ensure this animation is defined in the AnimationLibrary or anims/ section.`);
        }
    }
    // Unlike `autoplay`, which is only skipped, `current_animation` is applied
    // through `play()`: the ERR_FAIL_COND_MSG at animation_player.cpp:429 aborts
    // the assignment, and the property loads empty. Only claimed where the clip
    // set is fully enumerable, per `canCheckExistence` above.
    if (canCheckExistence && rawProps.current_animation !== undefined) {
        const currentName = literalText(rawProps.current_animation);
        if (currentName.length > 0 && !knownClips.has(currentName)) {
            reportArm(diagnostics, arms.currentAnimationMissing, node, `AnimationPlayer 'current_animation' references animation "${currentName}", which no library this file declares holds. Godot refuses the assignment, so the property loads empty.`);
        }
    }
    // `playback_default_blend_time` gets no advisory: linterParser.ts carries the
    // hint's 0..4096 as a warning-tier bound on the validator.
    // Godot raises no configuration warning (AnimationMixer declares no
    // get_configuration_warnings() override), but seek_internal returns at once on `!active`
    // (animation_player.cpp:664), so nothing this node declares reaches the scene.
    if (!isActive(rawProps)) {
        reportArm(diagnostics, arms.inactive, node, `AnimationPlayer 'active' is set to false. Animations will not play until this is set to true at runtime.`);
    }
    // `root_node` gets no diagnostic: animation_player.cpp declares it
    // PROPERTY_HINT_NONE and set_root_node is a bare assignment, so no path
    // shape is refused.
    return diagnostics;
}
/**
 * AnimationPlayer semantic validation rule
 */
const animationPlayerValidationRule = {
    meta: {
        name: 'valid-animationplayer-properties',
        description: 'Validates AnimationPlayer property values, animation references, and playback configuration',
        category: 'validation',
        applicableNodeTypes: ['AnimationPlayer'],
        emits: armEmits(arms),
    },
    check: checkAnimationPlayer,
};
ruleRegistry.register(animationPlayerValidationRule);
// Export for testing
//# sourceMappingURL=linter.js.map